import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { inventoryApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import ItemEditModal from './ItemEditModal'
import CsvImportModal from './CsvImportModal'

/**
 * Inventory admin — list, search, filter, and manage the literature catalog.
 *
 * Filters compose: category dropdown + search text + "new only" toggle. All
 * three round-trip to the API as query params, so the server does the work
 * and we only render what comes back.
 *
 * For now we render every result on one page (inventory has at most a few
 * hundred rows). If it grows past ~1000 we can add pagination later.
 *
 * Sort order applied client-side (the backend's ORDER BY treats year as text
 * which puts "10 Year" before "2 Year"). We sort by:
 *   category → medallion_type → year (natural numeric) → color → item_number
 * so medallion variants land together in proper year order.
 *
 * Editing (create / update / delete / image upload / CSV import + export) is
 * gated to sysadmins or users with the canUpload flag. Everyone else sees a
 * read-only catalog with "view only" in the actions column.
 */
export default function Inventory() {
    const { user, isSysAdmin } = useAuth()
    const canEdit = isSysAdmin || user?.canUpload === true

    const [items, setItems] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [exporting, setExporting] = useState(false)

    // Filters
    const [filterCategory, setFilterCategory] = useState('')
    const [searchText, setSearchText] = useState('')
    const [filterNewOnly, setFilterNewOnly] = useState(false)

    // Modals
    const [editing, setEditing] = useState(null)   // null | {} | {...item}
    const [importing, setImporting] = useState(false)

    useEffect(() => { loadCategories() }, [])
    useEffect(() => { loadItems() }, [filterCategory, searchText, filterNewOnly])

    async function loadItems() {
        setLoading(true)
        try {
            const filters = {
                category: filterCategory || undefined,
                q: searchText.trim() || undefined,
                isNew: filterNewOnly ? true : undefined,
            }
            const response = await inventoryApi.list(filters)
            if (response.success) {
                setItems(response.items)
            } else {
                toast.error(response.message || 'Could not load items')
            }
        } catch {
            toast.error('Network error loading inventory')
        } finally {
            setLoading(false)
        }
    }

    async function loadCategories() {
        try {
            const response = await inventoryApi.listCategories()
            if (response.success) setCategories(response.categories)
        } catch {
            // Non-fatal — the dropdown just stays empty.
        }
    }

    async function handleDelete(item) {
        if (!confirm(`Delete "${item.description}"? This can't be undone.`)) return
        try {
            const response = await inventoryApi.delete(item.id)
            if (response.success) {
                toast.success('Item deleted')
                loadItems()
                loadCategories()
            } else {
                toast.error(response.message || 'Delete failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    async function handleExport() {
        setExporting(true)
        try {
            const result = await inventoryApi.exportCsv()
            toast.success(`Downloaded ${result.fileName}`)
        } catch (err) {
            toast.error(err?.message || 'Export failed')
        } finally {
            setExporting(false)
        }
    }

    function handleSaved() {
        setEditing(null)
        loadItems()
        loadCategories()  // categories may have changed
    }

    function handleImported() {
        setImporting(false)
        loadItems()
        loadCategories()
    }

    // Sort items client-side. The backend returns them in alphabetical
    // year-as-text order which puts "10 Year" before "2 Year" — wrong.
    // We re-sort here with a natural numeric comparator for year and
    // item_number so medallion variants line up properly.
    const sortedItems = useMemo(() => {
        const list = [...items]
        list.sort((a, b) => {
            // 1. Category alphabetical
            const cat = (a.category || '').localeCompare(b.category || '')
            if (cat !== 0) return cat

            // 2. Medallion type alphabetical (null/empty last)
            const aType = a.medallionType || '\uFFFF'
            const bType = b.medallionType || '\uFFFF'
            const type = aType.localeCompare(bType)
            if (type !== 0) return type

            // 3. Year natural numeric (1 < 2 < ... < 10 < 11)
            const year = naturalYearSort(a.year || '', b.year || '')
            if (year !== 0) return year

            // 4. Color alphabetical
            const color = (a.color || '').localeCompare(b.color || '')
            if (color !== 0) return color

            // 5. Item number natural (so 'TP2Y-Red' < 'TP10Y-Red')
            return naturalItemSort(a.itemNumber || '', b.itemNumber || '')
        })
        return list
    }, [items])

    // Pre-compute formatted cost so the table render stays simple.
    const formattedItems = sortedItems.map(it => ({
        ...it,
        formattedCost: '$' + Number(it.cost).toFixed(2),
        variantLabel: [it.year, it.color].filter(Boolean).join(' / ') || null,
    }))

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Inventory</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {canEdit
                            ? 'The literature catalog. Sysadmins and users with upload privileges can edit.'
                            : 'The literature catalog.'}
                    </p>
                </div>
                {canEdit && (
                    <div className="flex gap-2 flex-wrap">
                        <Button variant="secondary" onClick={handleExport} disabled={exporting}>
                            {exporting ? 'Exporting…' : 'Export CSV'}
                        </Button>
                        <Button variant="secondary" onClick={() => setImporting(true)}>Import CSV</Button>
                        <Button onClick={() => setEditing({})}>+ New Item</Button>
                    </div>
                )}
            </div>

            {/* Filters bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <Input
                        id="inv-search"
                        label="Search"
                        placeholder="Item number or description..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>

                <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <label className="flex items-center text-sm text-gray-700 pb-2">
                    <input
                        type="checkbox"
                        checked={filterNewOnly}
                        onChange={(e) => setFilterNewOnly(e.target.checked)}
                        className="mr-2"
                    />
                    New items only
                </label>

                {(searchText || filterCategory || filterNewOnly) && (
                    <button
                        onClick={() => { setSearchText(''); setFilterCategory(''); setFilterNewOnly(false) }}
                        className="text-sm text-ghasc-navy hover:underline pb-2"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : formattedItems.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    {items.length === 0 && !searchText && !filterCategory && !filterNewOnly
                        ? (canEdit
                            ? 'No items yet. Click "New Item" or "Import CSV" to add some.'
                            : 'No items in the catalog yet.')
                        : 'No items match those filters.'}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {formattedItems.map((item) => (
                            <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                {/* Top: image + identifying info */}
                                <div className="flex gap-3">
                                    {item.imageUrl ? (
                                        <img src={item.imageUrl} alt=""
                                            className="w-16 h-16 object-contain bg-gray-50 rounded flex-shrink-0" />
                                    ) : (
                                        <div className="w-16 h-16 bg-gray-100 rounded text-xs text-gray-400 flex items-center justify-center flex-shrink-0">
                                            —
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-1.5">
                                            <div className="text-sm font-medium text-gray-900 break-words flex-1">
                                                {item.description}
                                            </div>
                                            {item.isNew && (
                                                <span className="flex-shrink-0 inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-ghasc-tan/30 text-ghasc-navy">
                                                    new
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 font-mono mt-0.5">
                                            {item.itemNumber}
                                        </div>
                                        {item.variantLabel && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {item.variantLabel}
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {item.category}
                                        </div>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Cost</div>
                                        <div className="font-semibold text-ghasc-navy">{item.formattedCost}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Stock</div>
                                        <div className={`font-semibold ${item.quantity === 0 ? 'text-ghasc-danger' : 'text-gray-900'}`}>
                                            {item.quantity}
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 pt-2 border-t border-gray-100">
                                    {canEdit ? (
                                        <>
                                            <button
                                                onClick={() => setEditing(item)}
                                                className="flex-1 px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item)}
                                                className="flex-1 px-3 py-2 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100"
                                            >
                                                Delete
                                            </button>
                                        </>
                                    ) : (
                                        <span className="flex-1 text-center text-xs text-gray-400 py-2">view only</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Count footer */}
                    <div className="text-xs text-gray-500 pt-1">
                        Showing {formattedItems.length} item{formattedItems.length === 1 ? '' : 's'}
                    </div>
                </>
            )}

            {/* Modals — only rendered for editors, defense-in-depth */}
            {canEdit && editing !== null && (
                <ItemEditModal
                    item={editing}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={handleSaved}
                />
            )}

            {canEdit && importing && (
                <CsvImportModal
                    onClose={() => setImporting(false)}
                    onImported={handleImported}
                />
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Sort helpers — match the patterns used in Shop.jsx so admin & shop align
// ---------------------------------------------------------------------------

/**
 * Natural sort for item numbers like 'TP2Y-Red' vs 'TP10Y-Red'.
 * Pulls out leading-digit groups so '2' < '10' numerically.
 */
function naturalItemSort(a, b) {
    if (!a) return 1
    if (!b) return -1
    // Split on digit groups so 'TP10Y' parses as ['TP', '10', 'Y']
    const tokenize = (s) => s.match(/(\d+|\D+)/g) || []
    const ta = tokenize(a)
    const tb = tokenize(b)
    const len = Math.min(ta.length, tb.length)
    for (let i = 0; i < len; i++) {
        const xa = ta[i]
        const xb = tb[i]
        const na = parseInt(xa, 10)
        const nb = parseInt(xb, 10)
        if (!isNaN(na) && !isNaN(nb)) {
            if (na !== nb) return na - nb
        } else {
            const cmp = xa.localeCompare(xb)
            if (cmp !== 0) return cmp
        }
    }
    return ta.length - tb.length
}

/**
 * Natural sort for year/time labels like '1 Year', '18 Month', '10 Year',
 * 'Welcome', 'Multiple Years'. Converts to a single sortable scalar:
 *   Welcome  → 0
 *   N day    → N
 *   N month  → N * 30
 *   N year   → N * 365
 *   Multiple → 9_000_000  (after all real years)
 *   anything else → 9_500_000 (after Multiple, alphabetical among themselves)
 * Items with no year sort to the very end.
 */
function naturalYearSort(a, b) {
    const parseLabel = (s) => {
        if (!s) return [Number.MAX_SAFE_INTEGER, '']
        const lower = s.trim().toLowerCase()

        if (/^welcome/.test(lower)) return [0, s]
        if (/^multi(ple)?/.test(lower)) return [9_000_000, s]

        const m = /^(\d+)\s*(day|month|year)s?/i.exec(s)
        if (m) {
            const num = parseInt(m[1], 10)
            const unit = m[2].toLowerCase()
            if (unit === 'day') return [num, s]
            if (unit === 'month') return [num * 30, s]
            if (unit === 'year') return [num * 365, s]
        }

        return [9_500_000, s]
    }
    const [an, as] = parseLabel(a)
    const [bn, bs] = parseLabel(b)
    if (an !== bn) return an - bn
    return as.localeCompare(bs)
}